import type { IconName } from '@/shared/ui/Icon'
import type { ModuleKey, Role } from '@/shared/api/types'
import type { Capability } from '@/shared/auth/capabilities'

/**
 * Navigation : un menu et un accueil par rôle livré.
 *
 * Le superadmin peut créer des rôles sur mesure, dont le nom est inconnu ici :
 * un repli couvre donc tout rôle absent de ces tables. Les entrées restent de
 * toute façon filtrées par les permissions et les modules, si bien qu'un menu
 * de repli n'affiche jamais un écran interdit.
 */

export interface Entry {
  path: string
  short: string
  title: string
  eyebrow: string
  icon: IconName
  capability?: Capability
  module?: ModuleKey
  counter?: 'alerts' | 'anomalies' | 'shifts'
}

export const SCREENS: Record<string, Entry> = {
  '/dashboard': {
    path: '/dashboard',
    short: 'Vue réseau',
    title: 'Vue consolidée du réseau',
    eyebrow: 'Direction',
    icon: 'dash',
  },
  '/my-station': {
    path: '/my-station',
    short: 'Ma station',
    title: 'Exploitation du jour',
    eyebrow: 'Station',
    icon: 'dash',
  },
  '/stations': {
    path: '/stations',
    short: 'Stations',
    title: 'Stations',
    eyebrow: 'Réseau',
    icon: 'station',
  },
  '/shifts': {
    path: '/shifts',
    short: 'Relevés & caisse',
    title: 'Relevés & caisse',
    eyebrow: 'Exploitation',
    icon: 'ticket',
    module: 'operations',
    counter: 'shifts',
  },
  '/tanks': {
    path: '/tanks',
    short: 'Stock & cuves',
    title: 'Stock & cuves',
    eyebrow: 'Exploitation',
    icon: 'tank',
    module: 'inventory',
  },
  '/equipment': {
    path: '/equipment',
    short: 'Matériel',
    title: 'Pompes, pistolets et cuves',
    eyebrow: 'Configuration',
    icon: 'tank',
    module: 'inventory',
    capability: 'assets.manage',
  },
  '/prices': {
    path: '/prices',
    short: 'Prix carburants',
    title: 'Carburants et prix de vente',
    eyebrow: 'Configuration',
    icon: 'coins',
    module: 'inventory',
    capability: 'prices.manage',
  },
  '/vouchers': {
    path: '/vouchers',
    short: 'Bons carburant',
    title: 'Bons carburant',
    eyebrow: 'Commercial',
    icon: 'ticket',
    module: 'vouchers',
    capability: 'vouchers.read',
  },
  '/procurement': {
    path: '/procurement',
    short: 'Approvisionnement',
    title: 'Approvisionnement & fournisseurs',
    eyebrow: 'Achats',
    icon: 'truck',
    module: 'procurement',
  },
  '/customers': {
    path: '/customers',
    short: 'Clients à crédit',
    title: 'Clients à crédit',
    eyebrow: 'Commercial',
    icon: 'users',
    module: 'credit',
    capability: 'customers.read',
  },
  '/anomalies': {
    path: '/anomalies',
    short: 'Anti-fraude',
    title: 'Sécurité & anti-fraude',
    eyebrow: 'Surveillance',
    icon: 'shield',
    module: 'risk',
    capability: 'anomalies.read',
    counter: 'anomalies',
  },
  '/alerts': {
    path: '/alerts',
    short: 'Alertes',
    title: 'Alertes',
    eyebrow: 'Surveillance',
    icon: 'bell',
    module: 'risk',
    counter: 'alerts',
  },
  '/maintenance': {
    path: '/maintenance',
    short: 'Maintenance',
    title: 'Maintenance des équipements',
    eyebrow: 'Exploitation',
    icon: 'wrench',
    module: 'maintenance',
  },
  '/inspections': {
    path: '/inspections',
    short: 'Contrôles',
    title: 'Contrôles sur site',
    eyebrow: 'Audit',
    icon: 'shield',
    module: 'inspections',
    capability: 'inspections.read',
  },
  '/shop': {
    path: '/shop',
    short: 'Boutique',
    title: 'Boutique',
    eyebrow: 'Exploitation',
    icon: 'cart',
    module: 'shop',
    capability: 'shop.view',
  },
  '/reports': {
    path: '/reports',
    short: 'Rapports',
    title: 'Rapports & analyses',
    eyebrow: 'Pilotage',
    icon: 'chart',
    capability: 'reports.view',
    module: 'reports',
  },
  '/loyalty': {
    path: '/loyalty',
    short: 'Fidélité',
    title: 'Programme de fidélité',
    eyebrow: 'Commercial',
    icon: 'star',
    module: 'loyalty',
  },
  '/financing': {
    path: '/financing',
    short: 'Financement',
    title: 'Financement & assurance',
    eyebrow: 'Services',
    icon: 'bank',
    module: 'financing',
  },
  '/integrations': {
    path: '/integrations',
    short: 'Intégrations',
    title: 'Intégrations & API',
    eyebrow: 'Plateforme',
    icon: 'plug',
    module: 'integrations',
    capability: 'modules.manage',
  },
  '/admin/users': {
    path: '/admin/users',
    short: 'Utilisateurs',
    title: 'Utilisateurs et rôles',
    eyebrow: 'Administration',
    icon: 'users',
    capability: 'users.manage',
  },
  '/admin/roles': {
    path: '/admin/roles',
    short: 'Rôles',
    title: 'Rôles et permissions',
    eyebrow: 'Administration',
    icon: 'shield',
    capability: 'roles.manage',
  },
  '/admin/modules': {
    path: '/admin/modules',
    short: 'Modules',
    title: 'Modules activés',
    eyebrow: 'Administration',
    icon: 'gear',
    capability: 'modules.manage',
  },
  '/admin/audit': {
    path: '/admin/audit',
    short: "Journal d'audit",
    title: "Journal d'audit",
    eyebrow: 'Administration',
    icon: 'doc',
    capability: 'audit.read',
  },
  '/mobile': {
    path: '/mobile',
    short: 'Application mobile',
    title: 'Application mobile',
    eyebrow: 'Terrain',
    icon: 'phone',
  },
}

export interface Group {
  title: string
  entries: string[]
}

const FALLBACK_MENU: Group[] = [
  { title: 'Pilotage', entries: ['/dashboard', '/stations', '/reports'] },
  { title: 'Surveillance', entries: ['/anomalies', '/alerts', '/inspections'] },
  {
    title: 'Exploitation',
    entries: ['/shifts', '/tanks', '/procurement', '/maintenance', '/shop'],
  },
  { title: 'Commercial', entries: ['/customers', '/vouchers', '/loyalty', '/financing'] },
  {
    title: 'Configuration', entries: ['/equipment', '/prices'],
  },
  {
    title: 'Administration',
    entries: ['/admin/users', '/admin/modules', '/admin/audit', '/integrations', '/mobile'],
  },
]

export const MENUS: Record<string, Group[]> = {
  /*
   * Le superadmin n'exploite pas le réseau : il ouvre les comptes, compose les
   * rôles et décide des modules souscrits. Lui afficher les écrans
   * d'exploitation reviendrait à en faire un utilisateur du quotidien, ce
   * qu'il n'est pas — et à noyer les trois écrans qui le concernent. L'API,
   * elle, ne lui refuse rien : c'est le recours quand plus rien ne passe.
   */
  super_admin: [
    {
      title: 'Administration',
      entries: ['/admin/users', '/admin/roles', '/admin/modules', '/admin/audit'],
    },
  ],
  director: [
    { title: 'Pilotage', entries: ['/dashboard', '/stations', '/reports'] },
    { title: 'Surveillance', entries: ['/anomalies', '/alerts', '/inspections'] },
    {
      title: 'Exploitation',
      entries: ['/shifts', '/tanks', '/procurement', '/maintenance', '/shop'],
    },
    { title: 'Commercial', entries: ['/customers', '/vouchers', '/loyalty', '/financing'] },
    { title: 'Configuration', entries: ['/equipment', '/prices'] },
    {
      title: 'Administration',
      entries: [
        '/admin/users',
        '/admin/roles',
        '/admin/modules',
        '/admin/audit',
        '/integrations',
        '/mobile',
      ],
    },
  ],
  manager: [
    { title: 'Ma station', entries: ['/my-station', '/shifts', '/tanks'] },
    { title: 'Exploitation', entries: ['/procurement', '/maintenance', '/shop'] },
    { title: 'Commercial', entries: ['/customers', '/vouchers'] },
    { title: 'Suivi', entries: ['/alerts', '/reports', '/mobile'] },
  ],
  controller: [
    { title: 'Audit', entries: ['/inspections', '/anomalies'] },
    { title: 'Vérification', entries: ['/shifts', '/tanks', '/stations'] },
    { title: 'Suivi', entries: ['/alerts', '/admin/audit', '/reports'] },
  ],
  accountant: [
    { title: 'Comptabilité', entries: ['/customers', '/vouchers'] },
    { title: 'Justificatifs', entries: ['/procurement', '/shifts', '/shop'] },
    { title: 'Édition', entries: ['/reports'] },
  ],
  pump_attendant: [
    { title: 'Terrain', entries: ['/mobile'] },
    { title: 'Mes postes', entries: ['/shifts'] },
  ],
}

export const HOME: Record<string, string> = {
  super_admin: '/admin/users',
  director: '/dashboard',
  manager: '/my-station',
  controller: '/inspections',
  accountant: '/customers',
  pump_attendant: '/shifts',
}

/** Menu du rôle, ou menu complet pour un rôle sur mesure. */
export function menuFor(role: Role): Group[] {
  return MENUS[role] ?? FALLBACK_MENU
}

/** Écran d'accueil du rôle. `/stations` est ouvert à tout compte connecté. */
export function homeFor(role: Role): string {
  return HOME[role] ?? '/stations'
}

export function titleFor(pathname: string): { title: string; eyebrow: string } {
  const exact = SCREENS[pathname]
  if (exact) return { title: exact.title, eyebrow: exact.eyebrow }
  const parent = Object.values(SCREENS)
    .filter((e) => pathname.startsWith(e.path + '/'))
    .sort((a, b) => b.path.length - a.path.length)[0]
  return parent
    ? { title: parent.title, eyebrow: parent.eyebrow }
    : { title: 'Sahel Fuel Control', eyebrow: 'Réseau' }
}
